const ItemType = require('@/lib/Items/ItemType');

/**
 * @constructor
 *
 * Handles making requests to the API to check the stock levels for matching products.
 *
 * A request is sent if:
 *  - Stock level checks are enabled for that Product type (products, food & drink products, both, or none).
 *  - A new checkable product has been added to the cart (that we have checked levels for already).
 *  - The check interval has expired.
 *
 * @param {ApiClient} apiClient
 * @param {ConnectivityChecker} connectivityChecker
 * @param {UserService} userService
 */
const ProductAvailabilityChecker = function (
    apiClient,
    connectivityChecker,
    userService
) {
    this.apiClient = apiClient;
    this.connectivityChecker = connectivityChecker;
    this.userService = userService;
};

ProductAvailabilityChecker.prototype = {
    /**
     * @param {Cart} cart
     * @param {CartService} cartService CartService is provided as a param to prevent a circular dependency.
     *
     * @returns {Promise}
     */
    check(cart, cartService) {
        // Find all items from the cart that we should be checking stock levels for.
        let cartLinesToCheck = this.getLinesToCheck(cart);

        // If there are no new items in the cart since the last check, and the last check was less than the specified
        // interval ago then we don't need to check.
        // This would occur either when adding an item and then quickly clicking the Pay button, or
        // when adding multiple lines of the product in quick succession (e.g. food with options)
        if (
            !this.hasNewLinesToCheck(cartLinesToCheck, cart)
            && !cart.cartProductStockLevels.hasStockCheckIntervalElapsed()
        ) {
            cartLinesToCheck = [];
        }

        if (cartLinesToCheck.length < 1) {
            // Nothing to do.
            return Promise.resolve({});
        }

        return this.sendRequest(cartLinesToCheck)
            .then((response) => {
                // We will need to handle the response here and update the line if the stock check fails.
                response.forEach((stockLevel) => {
                    cart.cartProductStockLevels.setAvailableQty(
                        stockLevel.branchID,
                        stockLevel.productID,
                        stockLevel.availableQty
                    );
                });
                cart.cartProductStockLevels.updateStockLastCheckedAt();

                cartService.calculate(cart);

                return response;
            })
            .catch((error) => {
                console.warn('Product availability check failed', error);
                throw error;
            });
    },

    /**
     * Find and return an array of items that we need to check stock levels for.
     *
     * @param {Cart} cart
     *
     * @returns {CartLine[]}
     */
    getLinesToCheck(cart) {
        return cart.getLines().filter((line) => {
            if (
                line.getItemType() === ItemType.PRODUCT
                && line.item.isFood
                && this.userService.company.apiStockControlFoodAndDrinkProducts
            ) {
                return true;
            }

            if (
                line.getItemType() === ItemType.PRODUCT
                && !line.item.isFood
                && this.userService.company.apiStockControlProductsExcludingFoodAndDrink
            ) {
                return true;
            }

            return false;
        });
    },

    /**
     * Check if the given array of cart lines contains any lines whose stock levels are not already contained
     * in the given cart.
     *
     * @param {CartLine[]} lines
     * @param {Cart} cart
     *
     * @returns {boolean}
     */
    hasNewLinesToCheck(lines, cart) {
        for (let i = 0; i < lines.length; i++) {
            if (!cart.getStockLevelForLine(lines[i])) {
                return true;
            }
        }

        return false;
    },

    /**
     * @param {CartLine[]} cartLinesToCheck
     *
     * @return {Promise<{branchID: number, productID: number, availableQty: number}[]>}
     */
    async sendRequest(cartLinesToCheck) {
        if (!this.connectivityChecker.isOnline()) {
            return Promise.reject(new Error('Offline'));
        }

        let results = [];

        // Sort lines by branchID.
        let linesByBranchID = {};
        cartLinesToCheck.forEach((line) => {
            if (!linesByBranchID.hasOwnProperty(line.branch.ID)) {
                linesByBranchID[line.branch.ID] = [];
            }

            linesByBranchID[line.branch.ID].push(line);
        });

        // Make one API request per item branch in use to check the stock levels
        // for all the items from that branch in the cart.
        // Combine the responses from all branches and return a single array of results.
        await Promise.all(
            Object.keys(linesByBranchID).map((branchID) => {
                const branchProductIDs = linesByBranchID[branchID].map((line) => line.item.ID);

                return this.apiClient
                    .request(
                        'GET',
                        'productsavailability',
                        {
                            branchID,
                            productIDs: branchProductIDs.join(','),
                        }
                    )
                    .then((response) => {
                        response.data.forEach((result) => results.push(result));
                    });
            })
        );

        return results;
    },
};

module.exports = ProductAvailabilityChecker;
